/**
 * Pure codec selection — no dependency on the filter config bundle, so the
 * engine is unit-testable in a plain node env. Present chooses from a field's
 * declared `SearchFilterType` (passed in as its string value) plus the raw value
 * shape; parse chooses from the readable JSON value shape.
 */
import { CodecKind } from './constants';

/** Store field keys that are search-wide, not filters. */
export const GLOBAL_KEYS = new Set([
  'value',
  'type',
  'modality',
  'page',
  'size',
]);

/**
 * SearchFilterType string values we branch on. Kept as literals (not imported
 * from ../types) so this module stays free of the heavy config/UI import graph.
 * These mirror the `const enum SearchFilterType` values.
 */
const T = {
  NUMBER_RANGE: 'NUMBER_RANGE',
  YEAR_RANGE: 'YEAR_RANGE',
  DATE_RANGE: 'DATE_RANGE',
  DURATION: 'DURATION',
  EMPLOYEE_BY_DEPARTMENT: 'EMPLOYEE_BY_DEPARTMENT',
  SWITCH: 'SWITCH',
  COMPANY_IDS: 'COMPANY_IDS',
  CUSTOM_SELECT_TREE: 'CUSTOM_SELECT_TREE',
  CUSTOM_SELECT: 'CUSTOM_SELECT',
  CHECK_BOX: 'CHECK_BOX',
  CHECK_BOX_GROUP: 'CHECK_BOX_GROUP',
  BUTTON_GROUP: 'BUTTON_GROUP',
  RADIO_GROUP: 'RADIO_GROUP',
  TEXT_FIELD_GROUP: 'TEXT_FIELD_GROUP',
  BULK: 'BULK',
  TEXT_FIELD: 'TEXT_FIELD',
  RADIO: 'RADIO',
  TIME_FRAME: 'TIME_FRAME',
} as const;

const LIST_TYPES = new Set<string>([
  T.CUSTOM_SELECT,
  T.CHECK_BOX,
  T.CHECK_BOX_GROUP,
  T.BUTTON_GROUP,
  T.RADIO_GROUP,
  T.TEXT_FIELD_GROUP,
  T.BULK,
]);

export function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v != null && !Array.isArray(v);
}

/** Is this a field we recognise (mappable, global, or a known companion key)? */
export function isKnownField(
  storeKey: string,
  knownFields: ReadonlySet<string> | { has(k: string): boolean }
): boolean {
  return (
    knownFields.has(storeKey) ||
    GLOBAL_KEYS.has(storeKey) ||
    storeKey === 'exact_match' ||
    /_(custom|sources|search_match|availability)$/.test(storeKey)
  );
}

/** Value-shape heuristic for fields not found in the config index. */
function heuristicKind(storeKey: string, raw: string): CodecKind {
  if (storeKey === 'exact_match') return CodecKind.MATCH_MAP;
  if (storeKey.endsWith('_custom')) return CodecKind.RANGE;
  if (raw === 'true' || raw === 'false') return CodecKind.BOOLEAN;
  if (raw.includes('::')) return CodecKind.ENTITY;
  if (raw.includes('^')) return CodecKind.LIST;
  return CodecKind.SINGLE;
}

/**
 * Fields whose codec is fixed by name (not by generic type or value shape) —
 * used by BOTH present and parse so the two agree. Covers employee-by-department,
 * headcount growth, and the keyword source/match companions.
 */
export function specialFieldKind(storeKey: string): CodecKind | undefined {
  if (storeKey === 'employee_by_department')
    return CodecKind.EMPLOYEE_BY_DEPARTMENT;
  if (storeKey === 'headcount_growth') return CodecKind.HEADCOUNT_GROWTH;
  if (storeKey.endsWith('_search_match')) return CodecKind.KEYWORD_MATCH;
  if (storeKey.endsWith('_sources')) return CodecKind.KEYWORD_SOURCES;
  // Keyword TEXT fields hold a `^`-joined list despite their TEXT_FIELD type.
  if (storeKey.endsWith('_keywords')) return CodecKind.LIST;
  return undefined;
}

/** Codec used to DECODE a store value to readable JSON. */
export function presentCodecKind(
  storeKey: string,
  fieldType: string | undefined,
  raw: string
): CodecKind {
  const special = specialFieldKind(storeKey);
  if (special) return special;
  switch (fieldType) {
    case T.NUMBER_RANGE:
    case T.YEAR_RANGE:
    case T.DATE_RANGE:
      return CodecKind.RANGE;
    case T.DURATION:
      return CodecKind.DURATION;
    case T.EMPLOYEE_BY_DEPARTMENT:
      return CodecKind.EMPLOYEE_BY_DEPARTMENT;
    case T.SWITCH:
      return CodecKind.BOOLEAN;
    case T.COMPANY_IDS:
      return CodecKind.ENTITY;
    case T.CUSTOM_SELECT_TREE:
      return CodecKind.TREE;
    default:
      if (fieldType && LIST_TYPES.has(fieldType))
        return raw.includes('::') ? CodecKind.ENTITY : CodecKind.LIST;
      // Everything else (text/radio/time-frame/unknown) goes through the
      // value-shape heuristic: a scalar value with a `^`/`::` separator is
      // really a list/composite and is surfaced as an array, not a raw string.
      return heuristicKind(storeKey, raw);
  }
}

/** Codec used to ENCODE a readable JSON value back to a store string. */
export function parseCodecKind(val: unknown): CodecKind {
  if (Array.isArray(val)) {
    if (val.length && val.every((x) => Array.isArray(x))) return CodecKind.TREE;
    if (val.length && val.every((x) => isPlainObject(x)))
      return CodecKind.ENTITY;
    return CodecKind.LIST;
  }
  if (typeof val === 'boolean') return CodecKind.BOOLEAN;
  if (isPlainObject(val)) {
    if ('from' in val || 'to' in val) return CodecKind.DURATION;
    if ('min' in val || 'max' in val) return CodecKind.RANGE;
    return CodecKind.MATCH_MAP;
  }
  return CodecKind.SINGLE;
}
